import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_MEMBERS = [
  { id: '1', name: 'John Smith', initials: 'JS', avatarUrl: null, role: 'Admin' },
  { id: '2', name: 'Sarah Johnson', initials: 'SJ', avatarUrl: null, role: 'Manager' },
  { id: '3', name: 'Mike Williams', initials: 'MW', avatarUrl: null, role: 'User' },
  { id: '4', name: 'Emma Brown', initials: 'EB', avatarUrl: null, role: 'User' },
  { id: '5', name: 'David Lee', initials: 'DL', avatarUrl: null, role: 'Manager' },
  { id: '6', name: 'Lisa Davis', initials: 'LD', avatarUrl: null, role: 'User' },
  { id: '7', name: 'James Wilson', initials: 'JW', avatarUrl: null, role: 'User' },
  { id: '8', name: 'Rachel Green', initials: 'RG', avatarUrl: null, role: 'User' },
  { id: '9', name: 'Tom Clark', initials: 'TC', avatarUrl: null, role: 'Admin' },
  { id: '10', name: 'Sophie Taylor', initials: 'ST', avatarUrl: null, role: 'User' },
  { id: '11', name: 'Oliver Martin', initials: 'OM', avatarUrl: null, role: 'Manager' },
  { id: '12', name: 'Emily White', initials: 'EW', avatarUrl: null, role: 'User' },
  { id: '13', name: 'Chris Anderson', initials: 'CA', avatarUrl: null, role: 'User' },
  { id: '14', name: 'Amy Thomas', initials: 'AT', avatarUrl: null, role: 'User' },
  { id: '15', name: 'Ben Harris', initials: 'BH', avatarUrl: null, role: 'User' },
  { id: '16', name: 'Kate Moore', initials: 'KM', avatarUrl: null, role: 'User' },
];

export function TeamDirectoryTile({ filter, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [data, setData] = useState({ members: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  // Observe container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (useMockData) {
      setData({ members: MOCK_MEMBERS, total: 24 });
      setLoading(false);
      return;
    }

    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const { data: members, count, error } = await supabase
          .from('company_members')
          .select(`
            id,
            role,
            user_profiles!inner(id, first_name, last_name, avatar_url)
          `, { count: 'exact' })
          .eq('company_id', company.id)
          .limit(50);

        if (error) throw error;

        const formatted = (members || []).map(m => ({
          id: m.user_profiles.id,
          name: `${m.user_profiles.first_name} ${m.user_profiles.last_name}`,
          initials: `${m.user_profiles.first_name?.[0] || ''}${m.user_profiles.last_name?.[0] || ''}`,
          avatarUrl: m.user_profiles.avatar_url,
          role: m.role,
        }));

        setData({ members: formatted, total: count || 0 });
      } catch (err) {
        console.error('Failed to load team directory:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, useMockData]);

  // Calculate layout based on dimensions
  const { width, height } = dimensions;
  const isXS = width < 120 || height < 80;
  const isSmall = width < 180 || height < 120;
  const isWide = width >= 400;
  const isLarge = width >= 300 && height >= 250;

  // Calculate optimal avatar size to fill the space
  const calculateLayout = () => {
    if (width === 0 || height === 0) return { size: 40, gap: 8, cols: 4, rows: 2, count: 8 };

    const footerHeight = isXS ? 0 : isSmall ? 0 : 28;
    const padding = 8;
    const availableHeight = height - footerHeight - padding;
    const availableWidth = width - padding;

    // Try different avatar sizes to find best fit
    const sizes = [56, 48, 44, 40, 36, 32, 28];
    const gap = 8;

    for (const size of sizes) {
      const avatarWithGap = size + gap;
      const cols = Math.floor(availableWidth / avatarWithGap);
      const rows = Math.floor(availableHeight / avatarWithGap);
      const count = cols * rows;

      // Use this size if it fits at least 4 avatars and fills space reasonably
      if (count >= 4 && cols >= 2) {
        return { size, gap, cols, rows, count };
      }
    }

    // Fallback for very small tiles
    const size = 28;
    const cols = Math.max(1, Math.floor(availableWidth / (size + gap)));
    const rows = Math.max(1, Math.floor(availableHeight / (size + gap)));
    return { size, gap, cols, rows, count: cols * rows };
  };

  const layout = calculateLayout();

  if (loading) {
    return (
      <div ref={containerRef} className="h-full flex items-center justify-center">
        <div className="flex flex-wrap gap-2 justify-center p-2">
          {[...Array(Math.min(8, layout.count))].map((_, i) => (
            <div
              key={i}
              className="rounded-full bg-gray-200 animate-pulse"
              style={{ width: layout.size, height: layout.size }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (data.members.length === 0) {
    return (
      <div ref={containerRef} className="h-full flex flex-col items-center justify-center text-gray-400">
        <Users className="h-8 w-8 mb-2" />
        <span className="text-sm">No team members</span>
      </div>
    );
  }

  const visibleMembers = data.members.slice(0, layout.count - 1); // Reserve 1 spot for "+X"
  const remaining = data.total - visibleMembers.length;

  // XS Layout: Just count
  if (isXS) {
    return (
      <div ref={containerRef} className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-700">{data.total}</div>
          <p className="text-xs text-gray-500">members</p>
        </div>
      </div>
    );
  }

  // List view for wide layouts
  if (isWide && isLarge) {
    const listCount = Math.floor((height - 40) / 44);
    const listMembers = data.members.slice(0, listCount);
    const listRemaining = data.total - listMembers.length;

    return (
      <div ref={containerRef} className="h-full flex flex-col p-2">
        <div className="flex-1 overflow-hidden">
          {listMembers.map((member) => (
            <div
              key={member.id}
              onClick={() => navigate(`/profile/${member.id}`)}
              className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={member.avatarUrl} />
                <AvatarFallback className="text-xs font-medium bg-blue-100 text-blue-700">
                  {member.initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{member.name}</p>
                <p className="text-xs text-gray-500 capitalize">{member.role}</p>
              </div>
            </div>
          ))}
        </div>

        {listRemaining > 0 && (
          <button
            onClick={() => navigate('/company-team')}
            className="mt-2 pt-2 border-t text-center text-sm text-blue-600 hover:text-blue-700"
          >
            +{listRemaining} more
          </button>
        )}
      </div>
    );
  }

  // Grid view for other layouts - use CSS grid to fill space evenly
  const gridItems = [...visibleMembers];
  if (remaining > 0) {
    gridItems.push({ id: 'remaining', isRemaining: true, count: remaining });
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col p-1">
      {/* Avatar Grid - using CSS grid for even distribution */}
      <div
        className="flex-1 grid place-items-center"
        style={{
          gridTemplateColumns: `repeat(${layout.cols}, ${layout.size}px)`,
          gridTemplateRows: `repeat(${layout.rows}, ${layout.size}px)`,
          gap: layout.gap,
          justifyContent: 'center',
          alignContent: 'center',
        }}
      >
        {gridItems.slice(0, layout.count).map((item) => (
          item.isRemaining ? (
            <div
              key="remaining"
              onClick={() => navigate('/company-team')}
              className="rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium cursor-pointer hover:bg-gray-300 transition-colors text-xs"
              style={{ width: layout.size, height: layout.size }}
            >
              +{item.count}
            </div>
          ) : (
            <div
              key={item.id}
              onClick={() => navigate(`/profile/${item.id}`)}
              className="group relative cursor-pointer"
            >
              <Avatar
                className="transition-transform group-hover:scale-110"
                style={{ width: layout.size, height: layout.size }}
              >
                <AvatarImage src={item.avatarUrl} />
                <AvatarFallback className="text-xs font-medium bg-blue-100 text-blue-700">
                  {item.initials}
                </AvatarFallback>
              </Avatar>

              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                {item.name}
              </div>
            </div>
          )
        ))}
      </div>

      {/* Footer - only show if not too small */}
      {!isSmall && (
        <div className="pt-1 border-t flex items-center justify-between text-xs">
          <span className="text-gray-600">{data.total} members</span>
          <button
            onClick={() => navigate('/company-team')}
            className="text-blue-600 hover:text-blue-700"
          >
            View All
          </button>
        </div>
      )}
    </div>
  );
}
